import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { phone } from './palette';

// Desktop device frame for the preview: an iPhone-proportion 440×956 screen
// inside a bezel, scaled down as a whole (never reflowed) so it fits
// 100vh − 48px. The screen background follows the preview theme.

export const SCREEN_WIDTH = 440;
export const SCREEN_HEIGHT = 956;
const BEZEL = 11; // 10px padding + 1px edge
export const FRAME_WIDTH = SCREEN_WIDTH + BEZEL * 2;
export const FRAME_HEIGHT = SCREEN_HEIGHT + BEZEL * 2;
const VIEWPORT_MARGIN = 48;
const MIN_SCALE = 0.4;

export const fitScale = (viewportHeight) =>
  Math.min(
    1,
    Math.max(MIN_SCALE, (viewportHeight - VIEWPORT_MARGIN) / FRAME_HEIGHT)
  );

const useViewportHeight = () => {
  const [height, setHeight] = useState(() => window.innerHeight);
  useEffect(() => {
    const onResize = () => setHeight(window.innerHeight);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return height;
};

// Holds the scaled footprint so the layout around it sees the real size.
const Slot = styled.div`
  position: relative;
  flex-shrink: 0;
`;

const Device = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: ${FRAME_WIDTH}px;
  height: ${FRAME_HEIGHT}px;
  box-sizing: border-box;
  padding: 10px;
  border-radius: 62px;
  border: 1px solid rgba(255, 255, 255, 0.14);
  background: linear-gradient(
    150deg,
    rgb(52, 52, 61) 0%,
    rgb(24, 24, 31) 38%,
    rgb(12, 12, 17) 100%
  );
  box-shadow: 0 30px 90px rgba(0, 0, 0, 0.7), 0 0 0 2px rgba(250, 70, 22, 0.14),
    inset 0 1px 1px rgba(255, 255, 255, 0.22);
  transform-origin: top left;
`;

const Screen = styled.div`
  position: relative;
  width: ${SCREEN_WIDTH}px;
  height: ${SCREEN_HEIGHT}px;
  border-radius: 52px;
  overflow: hidden;
  background: ${(p) => phone(p.theme).screen};
  box-shadow: inset 0 0 0 2.5px rgba(0, 0, 0, 0.9);
`;

const Notch = styled.div`
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 128px;
  height: 28px;
  border-radius: 0 0 16px 16px;
  background: black;
  z-index: 5;
`;

const PhoneFrame = ({ children }) => {
  const scale = fitScale(useViewportHeight());
  return (
    <Slot
      data-testid="phone-frame"
      style={{ width: FRAME_WIDTH * scale, height: FRAME_HEIGHT * scale }}
    >
      <Device style={{ transform: `scale(${scale})` }}>
        <Screen>
          <Notch aria-hidden="true" />
          {children}
        </Screen>
      </Device>
    </Slot>
  );
};

PhoneFrame.propTypes = {
  children: PropTypes.node.isRequired,
};

export default PhoneFrame;
